import { useState, useCallback } from 'react';
import { trackGoodFeedback, trackBadFeedback } from '../../../services/api/TrackingService';

/**
 * フィードバック機能を管理するカスタムフック
 * 
 * ユーザーからの良い/悪いフィードバックの送信とポップアップ表示状態を管理する。
 * フィードバックはTrackingServiceを通じてサーバーに送信される。
 * 
 * @param {string|null} queryId - 検索クエリを一意に識別するID（フィードバック送信時に使用）
 * @returns {Object} フィードバック関連の状態とハンドラ
 * @returns {string|null} returns.currentPopup - 現在表示中のポップアップ種別（'feedback' | 'badReason' | null）
 * @returns {boolean} returns.isFeedbackSubmitting - フィードバック送信中フラグ（二重送信防止用）
 * @returns {Function} returns.handleFeedbackClose - ポップアップを閉じるハンドラ
 * @returns {Function} returns.handleGoodFeedback - 良いフィードバック送信ハンドラ
 * @returns {Function} returns.handleBadFeedback - 悪いフィードバック選択ハンドラ（理由入力画面へ遷移）
 * @returns {Function} returns.handleFeedbackSubmit - 悪いフィードバック送信ハンドラ
 * @returns {Function} returns.handleFeedbackBack - 理由入力画面から初期画面に戻るハンドラ
 */
export const useFeedback = (queryId) => {
  /**
   * 現在表示中のポップアップ種別
   * - 'feedback': 初期フィードバック選択画面（良い/悪い選択）
   * - 'badReason': 悪いフィードバックの理由入力画面
   * - null: ポップアップ非表示
   */
  const [currentPopup, setCurrentPopup] = useState('feedback');

  /**
   * フィードバック送信中フラグ
   * trueの間は追加のフィードバック操作を無効化し、二重送信を防止
   */
  const [isFeedbackSubmitting, setIsFeedbackSubmitting] = useState(false);

  /**
   * フィードバックポップアップを閉じる
   * ポップアップ状態をnullに設定して非表示にする
   */
  const handleFeedbackClose = useCallback(() => {
    setCurrentPopup(null);
  }, []);

  /**
   * 良いフィードバックを送信する
   * 送信中は追加操作を無効化し、送信完了後にポップアップを閉じる
   * エラー発生時はコンソールにログ出力
   */
  const handleGoodFeedback = useCallback(async () => {
    // 送信中の場合は処理をスキップ（二重送信防止）
    if (isFeedbackSubmitting) return;
    setIsFeedbackSubmitting(true);
    try {
      await trackGoodFeedback(queryId, '引受目安検索結果詳細');
      // 送信成功後にポップアップを閉じる
      setCurrentPopup(null);
    } catch (error) {
      console.error('フィードバック送信エラー:', error);
    } finally {
      setIsFeedbackSubmitting(false);
    }
  }, [isFeedbackSubmitting, queryId]);

  /**
   * 悪いフィードバックを選択する
   * 理由入力画面（badReason）に遷移する
   * 送信中の場合は処理をスキップ
   */
  const handleBadFeedback = useCallback(() => {
    if (isFeedbackSubmitting) return;
    setCurrentPopup('badReason');
  }, [isFeedbackSubmitting]);

  /**
   * 悪いフィードバックを送信する
   * @param {Object} feedbackData - フィードバックデータ（理由など）
   * 送信中は追加操作を無効化し、送信完了後にポップアップを閉じる
   * エラー発生時はコンソールにログ出力
   */
  const handleFeedbackSubmit = useCallback(async (feedbackData) => {
    // 送信中の場合は処理をスキップ（二重送信防止）
    if (isFeedbackSubmitting) return;
    setIsFeedbackSubmitting(true);
    try {
      await trackBadFeedback(queryId, '引受目安検索結果詳細', feedbackData);
      // 送信成功後にポップアップを閉じる
      setCurrentPopup(null);
    } catch (error) {
      console.error('フィードバック送信エラー:', error);
    } finally {
      setIsFeedbackSubmitting(false);
    }
  }, [isFeedbackSubmitting, queryId]);

  /**
   * 理由入力画面から初期フィードバック選択画面に戻る
   * badReason画面からfeedback画面に遷移
   */
  const handleFeedbackBack = useCallback(() => {
    setCurrentPopup('feedback');
  }, []);

  return {
    currentPopup,
    isFeedbackSubmitting,
    handleFeedbackClose,
    handleGoodFeedback,
    handleBadFeedback,
    handleFeedbackSubmit,
    handleFeedbackBack,
  };
};

export default useFeedback;
